/**
 * 名片工具函数
 * 一次性完成：请求接口 → 解密数据 → 组装数据 → 返回最终结果
 */
import Request from 'utils/request';
import Util from 'utils';
import Config from 'config/Config';
import Stores from 'config/Stores';

// 全局缓存
let cachedCardInfo = null;
let cachedDecyptInfo = {};

/**
 * 获取并解密名片信息（核心函数）
 * 一次性完成所有工作：请求 → 解密 → 组装 → 返回
 *
 * @param {string} id - 名片 ID（可选）
 * @returns {Promise<Object|null>} 返回完整的解密后的名片信息
 *
 * @example
 * const cardInfo = await getDecryptedCardInfo();
 * console.log(cardInfo.name);  // 解密后的姓名
 * console.log(cardInfo.phone); // 解密后的手机号
 */
export async function getDecryptedCardInfo(id) {
  try {
    // 步骤 1: 检查是否有缓存且已解密（如果没有指定 id）
    if (!id && cachedCardInfo && cachedCardInfo.id) {
      // 检查缓存的数据是否已解密
      const isDecrypted = cachedCardInfo.phone && cachedCardInfo.phone.indexOf('*') === -1;
      if (isDecrypted) {
        console.log('使用缓存的名片信息');
        return cachedCardInfo;
      }
    }

    // 步骤 2: 从本地缓存获取之前的解密信息
    const localCache = Util.getLocalCache(Config.localStorageKeys.bussiness_card) || {};
    cachedDecyptInfo = localCache.decyptInfo || {};

    // 步骤 3: 请求名片接口（返回的数据可能是加密的）
    const res = await Request({
      host: Config.HOST_PROMOTE,
      url: 'userCard/detail/get',
      params: id ? { id } : undefined,
      ignoreErr: true
    });

    if (res.resultCode !== '0') {
      console.error('获取名片信息失败:', res.resultMsg);
      return null;
    }

    // 接口成功但没有名片数据（data 为 null），视为无名片信息，直接返回 null
    if (!res.data) {
      console.log('接口成功但无名片数据');
      return null;
    }

    // 步骤 4: 先合并已有的解密信息
    const cardInfo = { ...res.data, ...cachedDecyptInfo };

    // 步骤 5: 检查是否需要解密（phone 包含 * 表示是加密的）
    const isEncrypted = cardInfo.phone && cardInfo.phone.indexOf('*') !== -1;

    if (isEncrypted) {
      // 步骤 6: 解密手机号
      if (!cachedDecyptInfo.phone) {
        const decryptedPhone = await Stores.AppMod.getDecyptMsg({
          scene: 224,
          id: cardInfo.id,
          channal: ''
        });

        cachedDecyptInfo.phone = decryptedPhone;
        cardInfo.phone = decryptedPhone;
      } else {
        cardInfo.phone = cachedDecyptInfo.phone;
      }

      // 步骤 7: 解密姓名
      if (!cachedDecyptInfo.name) {
        const decryptedName = await Stores.AppMod.getDecyptMsg({
          scene: 125,
          id: cardInfo.id,
          channal: ''
        });

        cachedDecyptInfo.name = decryptedName;
        cardInfo.name = decryptedName;
      } else {
        cardInfo.name = cachedDecyptInfo.name;
      }

      // 步骤 8: 保存解密信息到本地缓存
      Util.setLocalCache(Config.localStorageKeys.bussiness_card, {
        decyptInfo: cachedDecyptInfo
      });
    }

    // 步骤 9: 更新全局缓存
    cachedCardInfo = cardInfo;

    // 步骤 10: 返回完整的解密后的数据
    return cardInfo;
  } catch (error) {
    console.error('获取并解密名片信息失败:', error);
    return null;
  }
}

/**
 * 清除名片缓存
 * 在退出登录时调用
 */
export function clearCardInfoCache() {
  cachedCardInfo = null;
  cachedDecyptInfo = {};
}



// WEBPACK FOOTER //
// ./src/utils/commonRequest/businessCard.js